import { Helmet } from 'react-helmet-async';
import { useEffect, useState, useCallback } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Container, Divider,
  MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import Iconify from '../../components/iconify';
import { useProject } from '../../hooks/ProjectContext';
import SitePicker from './SitePicker';

const SPECIES_OPTIONS = ['Aleppo Pine', 'Cypress', 'Oak', 'Eucalyptus', 'Mixed'];
const SLOPE_OPTIONS = ['flat', 'gentle', 'moderate', 'steep'];

function bearerHeaders() {
  const tkn = localStorage.getItem('tkn');
  return { 'Content-Type': 'application/json', ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}) };
}

export default function MeasureBaselinePage() {
  const { selectedProject, selectedSite, setSelectedSite } = useProject();
  const [strata, setStrata] = useState([]);
  const [latestRun, setLatestRun] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [savingId, setSavingId] = useState(null);

  const [fieldMeasurements, setFieldMeasurements] = useState([]);
  const [stocks, setStocks] = useState(null);

  const reload = useCallback(() => {
    if (!selectedSite?.id) return;
    setLoading(true); setError('');
    Promise.all([
      fetch(`/v1/flux/sites/${selectedSite.id}`, { headers: bearerHeaders() }).then(r => r.json()),
      fetch(`/v1/flux/sites/${selectedSite.id}/timeseries?range=90d`, { headers: bearerHeaders() }).then(r => r.json()),
    ])
      .then(([j, ts]) => {
        if (!j.ok) throw new Error(j.message);
        setStrata(j.strata || []);
        setLatestRun(j.screening?.[0] || null);
        setFieldMeasurements(j.fieldMeasurements || []);
        if (ts.ok) setStocks(ts.stocks);
      })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [selectedSite?.id]);

  useEffect(() => { reload(); }, [reload]);

  const updateStratum = useCallback(async (id, patch) => {
    setSavingId(id);
    try {
      const r = await fetch(`/v1/flux/strata/${id}`, {
        method: 'PUT', headers: bearerHeaders(), body: JSON.stringify(patch),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      setStrata(prev => prev.map(s => (s.id === id ? j.stratum : s)));
    } catch (e) { setError(e.message); }
    finally { setSavingId(null); }
  }, []);

  const runScreening = useCallback(async () => {
    if (!selectedSite?.id) return;
    setRunning(true); setError('');
    try {
      const r = await fetch(`/v1/flux/sites/${selectedSite.id}/screening`, {
        method: 'POST', headers: bearerHeaders(),
      });
      const j = await r.json();
      if (!j.ok) throw new Error(j.message);
      setLatestRun(j.run);
    } catch (e) { setError(e.message); }
    finally { setRunning(false); }
  }, [selectedSite?.id]);

  if (!selectedProject) return <EmptyState message="Pick a project from the header." />;
  if (!selectedSite) return (
    <SitePicker
      project={selectedProject}
      pageTitle="Baseline"
      pageDescription="Manual / low-frequency inputs. Drives the Tier 1 estimate when no live data exists yet."
      onPickSite={setSelectedSite}
    />
  );

  const results = latestRun?.results_json
    ? (typeof latestRun.results_json === 'string' ? JSON.parse(latestRun.results_json) : latestRun.results_json)
    : null;

  return (
    <>
      <Helmet><title>Baseline | Measure</title></Helmet>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box>
            <Typography variant="overline" color="text.secondary">{selectedProject.name} / {selectedSite.name}</Typography>
            <Typography variant="h4">Baseline</Typography>
            <Typography color="text.secondary" sx={{ mt: 0.5 }}>
              Manual / low-frequency inputs. Drives the Tier 1 estimate when no live data exists yet.
            </Typography>
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}

          {/* Carbon stocks breakdown */}
          {stocks && stocks.total_tCO2 > 0 && (
            <Box sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
              <Typography variant="overline" color="text.secondary">Carbon stocks (this site)</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' }, gap: 2, mt: 1 }}>
                <StockBlock label="Above-ground biomass (AGB)" value={stocks.agb_tCO2} unit="tCO₂" source={stocks.agb_source} />
                <StockBlock label="Below-ground biomass (BGB)" value={stocks.bgb_tCO2} unit="tCO₂" source={stocks.bgb_source} />
                <StockBlock label="Soil organic carbon (SOC)"   value={stocks.soc_tCO2} unit="tCO₂" source={stocks.soc_source} />
                <StockBlock label="TOTAL stock"                 value={stocks.total_tCO2} unit="tCO₂" source="Sum of all sources" highlight />
              </Box>
              {stocks.years_to_double && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                  At the current annual increase of {Math.round(stocks.annual_nee_tCO2)} tCO₂/yr, this stock would double in ~{stocks.years_to_double} years.
                </Typography>
              )}
            </Box>
          )}

          {/* Latest screening result */}
          {latestRun && (
            <Box sx={{ p: 2.5, borderRadius: 1.5, bgcolor: 'success.lighter' }}>
              <Typography variant="overline" color="success.dark">Latest screening (IPCC Tier 1)</Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mt: 0.5 }}>
                <Typography variant="h4" sx={{ fontWeight: 700, color: 'success.dark' }}>
                  {Math.round(latestRun.total_tco2_yr)} tCO₂/yr
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ±{latestRun.uncertainty_pct}% — {results?.range ? `${Math.round(results.range.low)} – ${Math.round(results.range.high)}` : '—'}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                Run {new Date(latestRun.created_at).toLocaleString()}
              </Typography>
            </Box>
          )}

          {/* Strata editor */}
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">Strata ({strata.length})</Typography>
              <Button
                variant="contained"
                startIcon={running ? <CircularProgress size={16} color="inherit" /> : <Iconify icon="eva:play-circle-fill" />}
                onClick={runScreening}
                disabled={running || strata.length === 0}
              >
                {running ? 'Running…' : 'Run Tier 1 screening'}
              </Button>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Edit a stratum's species, age, spacing, survival, or slope; then re-run screening to update the estimate.
            </Typography>

            {loading ? <CircularProgress size={24} /> : (
              <Stack spacing={1.5}>
                {strata.map(s => (
                  <StratumRow
                    key={s.id}
                    stratum={s}
                    saving={savingId === s.id}
                    onSave={patch => updateStratum(s.id, patch)}
                  />
                ))}
                {strata.length === 0 && (
                  <Alert severity="info">
                    No strata defined yet. Phase 1 will allow uploading a KML — for now the demo seed populates them.
                  </Alert>
                )}
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Field measurement campaigns */}
          <Box>
            <Typography variant="h6">Field measurements ({fieldMeasurements.length})</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Episodic uploads — typically once per season or year. SOC cores, allometric surveys, biomass estimates.
            </Typography>
            <Stack spacing={1}>
              {fieldMeasurements.map(fm => <FieldMeasurementRow key={fm.id} fm={fm} />)}
              {fieldMeasurements.length === 0 && (
                <Alert severity="info">No campaigns recorded yet. CSV upload coming in Phase 1.</Alert>
              )}
            </Stack>
          </Box>
        </Stack>
      </Container>
    </>
  );
}

function StratumRow({ stratum, saving, onSave }) {
  const [draft, setDraft] = useState(stratum);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stratum);

  useEffect(() => { setDraft(stratum); }, [stratum]);

  const set = (patch) => setDraft(d => ({ ...d, ...patch }));

  return (
    <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: dirty ? 'warning.main' : 'divider' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(7, 1fr) auto' }, gap: 1.5, alignItems: 'center' }}>
        <TextField label="Name" size="small" value={draft.name || ''} onChange={e => set({ name: e.target.value })} sx={{ gridColumn: { md: 'span 1' } }} />
        <TextField select label="Species" size="small" value={draft.species || ''} onChange={e => set({ species: e.target.value })}>
          {SPECIES_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField label="Planted" size="small" type="number" value={draft.planted_year || ''} onChange={e => set({ planted_year: +e.target.value || null })} />
        <TextField label="Spacing (m)" size="small" type="number" value={draft.spacing_m || ''} onChange={e => set({ spacing_m: +e.target.value || null })} />
        <TextField label="Survival %" size="small" type="number" value={draft.survival_pct || ''} onChange={e => set({ survival_pct: +e.target.value || null })} />
        <TextField select label="Slope" size="small" value={draft.slope_class || ''} onChange={e => set({ slope_class: e.target.value })}>
          <MenuItem value="">—</MenuItem>
          {SLOPE_OPTIONS.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
        </TextField>
        <TextField label="Area (ha)" size="small" type="number" value={draft.area_ha || ''} onChange={e => set({ area_ha: +e.target.value || null })} />
        <Button
          size="small" variant="contained" disabled={!dirty || saving}
          onClick={() => onSave({
            name: draft.name, species: draft.species, planted_year: draft.planted_year,
            spacing_m: draft.spacing_m, survival_pct: draft.survival_pct,
            slope_class: draft.slope_class, area_ha: draft.area_ha,
          })}
        >
          {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

function StockBlock({ label, value, unit, source, highlight }) {
  return (
    <Box sx={{
      p: 1.5, borderRadius: 1,
      bgcolor: highlight ? 'success.lighter' : 'transparent',
      border: highlight ? 'none' : '1px dashed',
      borderColor: 'divider',
    }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, color: highlight ? 'success.dark' : 'text.primary' }}>
        {value != null ? value.toLocaleString() : '—'} <Typography component="span" variant="caption" color="text.secondary">{unit}</Typography>
      </Typography>
      {source && <Typography variant="caption" color="text.disabled" sx={{ display: 'block', fontStyle: 'italic' }}>{source}</Typography>}
    </Box>
  );
}

function FieldMeasurementRow({ fm }) {
  const summary = typeof fm.summary_json === 'string' ? JSON.parse(fm.summary_json) : fm.summary_json;
  const typeLabels = { allometry: 'Allometry survey', soc: 'SOC cores', custom: 'Custom' };
  const headline = (() => {
    if (fm.measurement_type === 'allometry' && summary) {
      return `${summary.rows ?? fm.row_count} trees · DBH ${summary.avg_dbh_cm}cm · height ${summary.avg_height_m}m · AGB ${summary.agb_tha_estimate} t/ha`;
    }
    if (fm.measurement_type === 'soc' && summary) {
      return `${summary.cores_count ?? fm.row_count} cores @ ${summary.depth_cm}cm · SOC ${summary.avg_soc_pct}% · stock ${summary.soc_stock_tha} t/ha`;
    }
    return `${fm.row_count ?? '?'} rows`;
  })();
  return (
    <Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {typeLabels[fm.measurement_type] || fm.measurement_type}
            <Chip size="small" label={fm.campaign_date} variant="outlined" sx={{ ml: 1 }} />
          </Typography>
          <Typography variant="caption" color="text.secondary">{headline}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

function EmptyState({ message }) {
  return (
    <Container maxWidth="md">
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography variant="h5" sx={{ mb: 1 }}>Baseline</Typography>
        <Typography color="text.secondary">{message}</Typography>
      </Box>
    </Container>
  );
}
